import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MapPin, 
  ChevronRight,
  Loader2,
  Building2,
  Navigation,
  Image as ImageIcon,
  ChevronLeft,
  IndianRupee,
  Car,
  Droplets,
  Lightbulb,
  Coffee,
  Clock,
  Zap,
  TrendingUp,
  Star,
  Wifi,
  Shield,
  ArrowUpDown,
  MapPinned,
  Banknote,
  Activity
} from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';

// Default location (Mumbai city center) when user location not available
const DEFAULT_USER_LOCATION = {
  lat: 19.0760,
  lng: 72.8777
};

import { BACKEND_URL, API } from '../../utils/apiConfig';

// Vibrant color palette for venues
const VENUE_COLORS = [
  { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-50', text: 'text-violet-600' },
  { gradient: 'from-pink-500 to-rose-600', bg: 'bg-pink-50', text: 'text-pink-600' },
  { gradient: 'from-cyan-500 to-blue-600', bg: 'bg-cyan-50', text: 'text-cyan-600' },
  { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', text: 'text-amber-600' },
  { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', text: 'text-emerald-600' },
];

// Facility icons mapping
const FACILITY_ICONS = {
  parking: { icon: Car, label: 'Parking', color: 'text-blue-500', bg: 'bg-blue-50' },
  washroom: { icon: Droplets, label: 'Washroom', color: 'text-cyan-500', bg: 'bg-cyan-50' },
  lights: { icon: Lightbulb, label: 'Lights', color: 'text-amber-500', bg: 'bg-amber-50' },
  indoor: { icon: Building2, label: 'Indoor', color: 'text-purple-500', bg: 'bg-purple-50' },
  cafe: { icon: Coffee, label: 'Cafe', color: 'text-orange-500', bg: 'bg-orange-50' },
  wifi: { icon: Wifi, label: 'WiFi', color: 'text-green-500', bg: 'bg-green-50' },
  security: { icon: Shield, label: 'Security', color: 'text-slate-500', bg: 'bg-slate-100' }
};

// Availability status config
const AVAILABILITY_CONFIG = {
  available: { emoji: '🟢', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', priority: 1 },
  filling: { emoji: '🟡', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', priority: 2 },
  almost_full: { emoji: '🔴', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', priority: 3 }
};

// Sort options configuration
const SORT_OPTIONS = [
  { value: 'distance', label: 'Distance', icon: MapPinned, description: 'Nearest first' },
  { value: 'price', label: 'Price', icon: Banknote, description: 'Cheapest first' },
  { value: 'availability', label: 'Availability', icon: Activity, description: 'Most available first' }
];

export default function VenueSelectionPage() {
  const { isProfileComplete } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedGame = searchParams.get('game');
  
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(false);
  const [sortBy, setSortBy] = useState('distance'); // distance, price, availability

  // Get user's location for distance calculation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location not available:', error.message);
          setLocationError(true);
        },
        { timeout: 5000, maximumAge: 300000 }
      );
    }
  }, []);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Estimate travel time (rough: 20 km/h avg in city traffic)
  const estimateTravelTime = (distanceKm) => {
    if (!distanceKm) return null;
    const minutes = Math.round(distanceKm * 3); // ~20 km/h = 3 min per km
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  useEffect(() => {
    if (!isProfileComplete) {
      navigate('/profile-setup');
      return;
    }
    
    if (!selectedGame) {
      navigate('/');
      return;
    }
    
    fetchVenues();
  }, [isProfileComplete, selectedGame, navigate]);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/user/venues`, {
        params: { game: selectedGame }
      });
      setVenues(response.data.venues || []);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleVenueSelect = (venue) => {
    navigate(`/slots?game=${encodeURIComponent(selectedGame)}&venue_id=${venue.id}&venue_name=${encodeURIComponent(venue.name)}`);
  };

  const getVenueColor = (index) => VENUE_COLORS[index % VENUE_COLORS.length];

  // Sort venues based on selected criteria
  const sortedVenues = useMemo(() => {
    if (!venues.length) return [];
    
    const originLocation = userLocation || DEFAULT_USER_LOCATION;
    
    // Add computed distance to each venue for sorting
    const venuesWithDistance = venues.map(venue => ({
      ...venue,
      computedDistance: calculateDistance(
        originLocation.lat,
        originLocation.lng,
        venue.latitude,
        venue.longitude
      ) || 9999 // Default high value if no coordinates
    }));
    
    return [...venuesWithDistance].sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return a.computedDistance - b.computedDistance;
        case 'price':
          return (a.starting_price || 0) - (b.starting_price || 0);
        case 'availability':
          const priorityA = AVAILABILITY_CONFIG[a.availability_status]?.priority || 3;
          const priorityB = AVAILABILITY_CONFIG[b.availability_status]?.priority || 3;
          return priorityA - priorityB;
        default:
          return 0;
      }
    });
  }, [venues, sortBy, userLocation]);

  // Get venue image URL or placeholder
  const getVenueImage = (venue) => {
    if (venue.images && venue.images.length > 0) {
      // If it's a relative API path, prepend the API URL
      const img = venue.images[0];
      if (img.startsWith('/api/')) {
        return `${BACKEND_URL}${img}`;
      }
      return img;
    }
    return null;
  };

  // Image carousel component
  const VenueImageCarousel = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    if (!images || images.length === 0) return null;
    
    const getImageUrl = (img) => {
      if (img.startsWith('/api/')) {
        return `${BACKEND_URL}${img}`;
      }
      return img;
    };
    
    return (
      <div className="relative w-full h-32 rounded-xl overflow-hidden mb-3">
        <img 
          src={getImageUrl(images[currentIndex])}
          alt="Venue"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x200?text=Venue+Image';
          }}
        />
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((prev) => (prev + 1) % images.length);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, idx) => (
                <span 
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full ${idx === currentIndex ? 'bg-white' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      {/* Progress Steps - Vibrant */}
      <div className="bg-white/80 backdrop-blur border-b border-purple-100 px-4 py-3">
        <div className="flex items-center gap-1 text-xs">
          <span className="flex items-center gap-1 text-emerald-600">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white flex items-center justify-center text-xs font-bold shadow-md">✓</span>
            <span className="hidden sm:inline">Game</span>
          </span>
          <div className="flex-1 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full" />
          <span className="flex items-center gap-1 text-cyan-600 font-medium">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white flex items-center justify-center text-xs font-bold shadow-lg ring-2 ring-cyan-200">2</span>
            <span className="hidden sm:inline">Venue</span>
          </span>
          <div className="flex-1 h-1 bg-slate-200 rounded-full" />
          <span className="flex items-center gap-1 text-slate-400">
            <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">3</span>
            <span className="hidden sm:inline">Slot</span>
          </span>
          <div className="flex-1 h-1 bg-slate-200 rounded-full" />
          <span className="flex items-center gap-1 text-slate-400">
            <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">4</span>
            <span className="hidden sm:inline">Confirm</span>
          </span>
        </div>
      </div>

      {/* Header with Game Context */}
      <div className="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 text-white px-4 py-4">
        <p className="text-cyan-200 text-xs uppercase tracking-wide">Step 2 of 4</p>
        <h1 className="text-xl font-bold">Select Venue</h1>
        <p className="text-cyan-100 text-sm mt-1">
          Playing <span className="font-semibold text-white">{selectedGame}</span>
        </p>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        ) : venues.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-purple-100 shadow-sm">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Venues Available</h3>
            <p className="text-slate-500 mb-4">No venues found for {selectedGame}</p>
            <button
              onClick={() => navigate('/')}
              className="text-violet-600 font-semibold hover:underline"
            >
              ← Choose another game
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Sort By Selector */}
            <div className="bg-white rounded-xl p-3 shadow-sm border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpDown className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Sort by</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {SORT_OPTIONS.map((option) => {
                  const IconComponent = option.icon;
                  const isActive = sortBy === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      data-testid={`sort-${option.value}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isActive 
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <IconComponent className="w-3.5 h-3.5" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-1.5">
                {SORT_OPTIONS.find(o => o.value === sortBy)?.description}
              </p>
            </div>

            {/* Venue Cards */}
            {sortedVenues.map((venue, index) => {
              const color = getVenueColor(index);
              const hasImages = venue.images && venue.images.length > 0;
              
              // Use precomputed distance from sorting
              const distance = venue.computedDistance < 9999 ? venue.computedDistance : null;
              const travelTime = estimateTravelTime(distance);
              
              // Get availability config
              const availabilityConfig = AVAILABILITY_CONFIG[venue.availability_status] || AVAILABILITY_CONFIG.available;
              
              return (
                <button
                  key={venue.id}
                  onClick={() => handleVenueSelect(venue)}
                  className="w-full bg-white rounded-2xl p-4 shadow-md border border-purple-100 hover:shadow-xl hover:border-purple-200 transition-all text-left group overflow-hidden relative"
                  data-testid={`venue-card-${venue.id}`}
                >
                  {/* Background gradient on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${color.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                  
                  {/* Venue Images Carousel */}
                  {hasImages && (
                    <VenueImageCarousel images={venue.images} />
                  )}
                  
                  <div className="flex items-start gap-4 relative">
                    {/* Icon placeholder if no images */}
                    {!hasImages && (
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color.gradient} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        <MapPin className="w-7 h-7 text-white" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      {/* Venue Name */}
                      <h3 className="font-bold text-slate-900 group-hover:text-violet-600 transition-colors text-base">
                        {venue.name}
                      </h3>
                      
                      {/* Full Address */}
                      {(venue.full_address || venue.address) && (
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">
                          {venue.full_address || venue.address}
                        </p>
                      )}
                      
                      {/* Distance & Travel Time Row */}
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {distance && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <Navigation className="w-3 h-3 text-blue-500" />
                            <span className="font-medium">{distance.toFixed(1)} km</span>
                            {travelTime && (
                              <>
                                <span className="text-slate-300 mx-0.5">•</span>
                                <Car className="w-3 h-3 text-slate-400" />
                                <span>{travelTime}</span>
                              </>
                            )}
                          </span>
                        )}
                        
                        {/* Google Maps Link */}
                        {venue.google_maps_link && (
                          <a
                            href={venue.google_maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <MapPin className="w-3 h-3" />
                            Directions
                          </a>
                        )}
                      </div>
                      
                      {/* Starting Price & Availability Row */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {/* Starting Price */}
                        {venue.starting_price > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-semibold">
                            <IndianRupee className="w-3 h-3" />
                            {venue.starting_price}
                            <span className="font-normal text-emerald-600">/slot</span>
                          </span>
                        )}
                        
                        {/* Live Availability Indicator */}
                        <span className={`inline-flex items-center gap-1 text-xs ${availabilityConfig.bg} ${availabilityConfig.color} px-2.5 py-1 rounded-full font-medium border ${availabilityConfig.border}`}>
                          <span>{availabilityConfig.emoji}</span>
                          {venue.availability_label || 'Available'}
                        </span>
                        
                        {/* Court Count */}
                        <span className={`inline-flex items-center gap-1 text-xs ${color.bg} ${color.text} px-2.5 py-1 rounded-full font-medium`}>
                          <Building2 className="w-3 h-3" />
                          {venue.court_count} court{venue.court_count !== 1 ? 's' : ''}
                        </span>
                        
                        {/* Photos count if images exist */}
                        {hasImages && (
                          <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">
                            <ImageIcon className="w-3 h-3" />
                            {venue.images.length}
                          </span>
                        )}
                      </div>
                      
                      {/* Facility Highlights (Icons Row) */}
                      {venue.facilities && venue.facilities.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                          {venue.facilities.slice(0, 5).map((facility) => {
                            const facilityConfig = FACILITY_ICONS[facility.toLowerCase()];
                            if (!facilityConfig) return null;
                            const IconComponent = facilityConfig.icon;
                            return (
                              <span 
                                key={facility} 
                                className={`inline-flex items-center gap-1 text-xs ${facilityConfig.bg} ${facilityConfig.color} px-2 py-1 rounded-md`}
                                title={facilityConfig.label}
                              >
                                <IconComponent className="w-3 h-3" />
                                <span className="hidden sm:inline">{facilityConfig.label}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-6 h-6 text-slate-300 group-hover:text-violet-500 group-hover:translate-x-1 transition-all flex-shrink-0 mt-3" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <CustomerFooter />
    </div>
  );
}
